import { useEffect, useMemo, useState } from "react";
import Modal from "../ui/Modal";
import Loader from "../ui/Loader";
import useFinancialReportData from "../../hooks/useFinancialReportData";
import { formatCurrencyValue } from "../../services/exportTratamenteService";

const formatCurrency = (value) =>
  formatCurrencyValue(Number.isFinite(Number(value)) ? Number(value) : 0);

export default function DoctorFinancialModal({ onClose }) {
  const { loading, error, monthOrder, monthlyData } = useFinancialReportData();
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);

  useEffect(() => {
    if (!monthOrder.length) return;
    setSelectedMonthKey((prev) =>
      prev && monthOrder.includes(prev) ? prev : monthOrder[0]
    );
  }, [monthOrder]);

  const currentMonthData = useMemo(() => {
    if (!selectedMonthKey || !monthlyData[selectedMonthKey]) {
      return {
        label: "",
        commissions: [],
        totalCommission: 0,
      };
    }
    return monthlyData[selectedMonthKey];
  }, [selectedMonthKey, monthlyData]);

  const monthLabels = useMemo(() => {
    const labels = {};
    monthOrder.forEach((key) => {
      const rawLabel = monthlyData[key]?.label || key || "";
      labels[key] = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
    });
    return labels;
  }, [monthOrder, monthlyData]);

  const commissionEntries = useMemo(() => {
    const entries = [];
    (currentMonthData.commissions || []).forEach((block) => {
      (block.entries || []).forEach((entry) => {
        entries.push({
          patientName: entry.patientName,
          category: entry.category,
          amount: entry.amount,
          date: entry.date,
          teeth: Array.isArray(entry.teeth) ? entry.teeth : [],
        });
      });
    });

    return entries
      .filter((entry) => entry.patientName)
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
  }, [currentMonthData]);

  const renderMonthSelect = (value, onChange) =>
    monthOrder.length ? (
      <select
        className="border border-blue-200 rounded-lg bg-white px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 inline-block w-auto"
        value={value || monthOrder[0]}
        onChange={(event) => onChange(event.target.value)}
      >
        {monthOrder.map((key) => (
          <option key={key} value={key}>
            {monthLabels[key] || key}
          </option>
        ))}
      </select>
    ) : (
      <select className="border rounded px-3 py-2 text-sm" disabled>
        <option>Fără date</option>
      </select>
    );

  const renderContent = () => {
    if (!monthOrder.length || !selectedMonthKey) {
      return (
        <p className="text-sm text-gray-600">
          Nu există date financiare disponibile momentan.
        </p>
      );
    }

    return (
      <div className="space-y-5">
        <p className="text-base sm:text-lg font-semibold text-gray-900">
          {monthLabels[selectedMonthKey] || "Luna curentă"}:{" "}
          <span className="text-blue-700">
            {formatCurrency(currentMonthData.totalCommission)} RON
          </span>
        </p>

        <div className="sm:max-w-xs pt-1">
          {renderMonthSelect(selectedMonthKey, setSelectedMonthKey)}
        </div>

        {commissionEntries.length === 0 ? (
          <p className="text-sm text-gray-600">
            Nu există comisioane pentru luna selectată.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-xs uppercase tracking-wide text-gray-500 text-left">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Pacient</th>
                  <th className="px-4 py-3">Zonă</th>
                  <th className="px-4 py-3">Procent</th>
                  <th className="px-4 py-3 text-right">Comision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white text-sm">
                {commissionEntries.map((entry, index) => {
                  const isRetreat = entry.category === "retratamente";
                  const formattedDate = entry.date
                    ? new Date(entry.date).toLocaleDateString("ro-RO", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                      })
                    : "-";
                  const teethLabel = Array.isArray(entry.teeth) && entry.teeth.length
                    ? entry.teeth.join(', ')
                    : '-';
                  return (
                    <tr key={`${entry.patientName}-${entry.category}-${index}`}>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formattedDate}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {entry.patientName}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {teethLabel}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="hidden sm:inline">{isRetreat ? "10%" : "20%"}</span>
                        <span className="sm:hidden">{isRetreat ? "10" : "20"}%</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-blue-700 whitespace-nowrap">
                        {formatCurrency(entry.amount)} RON
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal title="Raport lunar" onClose={onClose} size="lg">
      {loading ? (
        <Loader text="Se încarcă datele financiare..." />
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        renderContent()
      )}
    </Modal>
  );
}
